/**
 * Плашки строки реестра и карточки проекта (US-26).
 *
 * `StatusPill` — приведённый статус проекта (FR-3.9). Приведение сырых
 * статусов госзакупок выполняет загрузка, экран их не разбирает
 * (US-27 FR-5.1); значения выдачи — `IN_PROGRESS` · `COMPLETED` ·
 * `SUSPENDED`, `null` означает «таблица соответствия значение не
 * покрыла» и плашкой не рисуется. Четвёртый статус экрана «Расторгнут»
 * (решение 1) в сегодняшней выдаче не встречается: ключ `TERMINATED`
 * заведён заранее, чтобы его появление не уронило вёрстку.
 *
 * `ProjectTypePill` — метка типа проекта «Переходящий» / «Новый»
 * (FR-3.8); тип неизвестен → метка не показывается.
 *
 * `SupervisionOnlyBadge` — метка «только надзор» у проекта без договора
 * СМР (FR-3.1.1). Признак — отдельное поле выдачи, а не вывод экрана из
 * состава договоров (US-27 FR-4.5.1); в DP-446 поля пока нет, поэтому
 * метка не показывается, пока оно не появится.
 */
import { useTranslation } from 'react-i18next';

import { cn } from '../../../lib/cn';

/** Точка-индикатор статуса; ключи — значения выдачи (US-27 FR-5.1). */
const DOT: Record<string, string> = {
  IN_PROGRESS: 'bg-chart-indigo',
  COMPLETED: 'bg-brand-500',
  TERMINATED: 'bg-status-danger',
  SUSPENDED: 'bg-status-warning',
};

export const StatusPill = ({ status }: { status: string | null }) => {
  const { t } = useTranslation();
  if (status === null) return null;
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 whitespace-nowrap rounded-full border border-surface-border px-2.5 py-0.5 text-xs',
        status === 'COMPLETED' ? 'text-brand-700' : 'text-text-muted',
      )}
      data-testid={`projects-status-${status}`}
    >
      <span
        className={cn('size-1.5 rounded-full', DOT[status] ?? 'bg-text-faint')}
        aria-hidden="true"
      />
      {t(`projects.registry.statuses.${status}`, { defaultValue: status })}
    </span>
  );
};

/** Тип проекта: метка показывается только когда тип известен (FR-3.8). */
export const ProjectTypePill = ({ type }: { type: string | null }) => {
  const { t } = useTranslation();
  if (type === null) return null;
  return (
    <span
      className={cn(
        'inline-flex whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-semibold',
        type === 'NEW'
          ? 'bg-brand-50 text-brand-700'
          : 'bg-surface-muted text-text-muted',
      )}
      data-testid={`projects-type-${type}`}
    >
      {t(`projects.registry.types.${type}`, { defaultValue: type })}
    </span>
  );
};

/** «Только надзор» — стоимость проекта явно неполна (FR-3.1.1). */
export const SupervisionOnlyBadge = () => {
  const { t } = useTranslation();
  return (
    <span
      className="ml-1.5 inline-flex whitespace-nowrap rounded-full bg-status-warning/15 px-2 py-0.5 text-xs font-semibold text-status-warning"
      title={t('projects.registry.supervisionOnlyHint')}
      data-testid="projects-supervision-only"
    >
      {t('projects.registry.supervisionOnly')}
    </span>
  );
};
